import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import DossiersLayout from "./DossiersLayout";
import {
    AuthUser,
    Dossier,
    DossierDetailType,
    DossierStatus,
    DossierStatusEnum,
    Establishment,
    EstablishmentCategory,
    EstablishmentDbType,
    RolesEnum,
    Session,
    TabMenu,
} from "../../types";
import { dossierStorageKey, sessionStorageKey } from "../../consts";
import { useFetch, useLocalStorage } from "../../hooks";
import {
    filterDossierStatusByRole,
    getDossierStatusByCode,
    getDossierStatusOfDossier,
    getDossierStatusesIdsByUserRole,
} from "./utils";
import {
    getDossiers,
    getEstablishmentByIds,
    getReferenciels,
    getInstructorList,
    dossiersAssignment,
    getUsers,
} from "../../services";
import { cloneObject, groupBy, scrollToTop } from "../../helpers";
import DossiersList from "./DossiersList";
import { useDossier } from "./useDossier";
import EmptyDossiers from "./Dossier/EmptyDossiers/EmptyDossiers";
import { CustomModalContext } from "../../contexts";
import BulkAssigned from "./BulkAssigned/BulkAssigned";

export default function Dossiers(dossierStatusFilter: any = { code: "" }) {
    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        auth: { user: currentUser, currentUserRoleId: userRoleId },
        [dossierStorageKey]: { dossierStatus },
    } = session;

    const establishmentGroup = currentUser.establishmentGroup;
    const region = currentUser.region;
    const filterTabsMenu: TabMenu[] = [{ key: "tous", name: "Tous" }]
        .concat(
            filterDossierStatusByRole(dossierStatus, userRoleId).map(
                (d: DossierStatus) => {
                    return {
                        key: d.code,
                        name: d.label,
                        id: d.id,
                    };
                }
            )
        )
        .concat(
            userRoleId === RolesEnum.SYNERPA
                ? [{ name: "En cours d’examen", key: "EN_COURS_EXAMEN" }]
                : []
        );

    const filterDossiers = (
        filterActiveTab: string,
        dossiers: Dossier[],
        dossiersStatus: DossierStatus[],
        searchQuery?: string
    ) => {
        let dossiersTmp = dossiers;

        if (searchQuery && searchQuery.length > 2) {
            const regexp = new RegExp(searchQuery, "i");
            dossiersTmp = dossiers.filter((d: any) => {
                return (
                    regexp.test(d.establishment.companyName) ||
                    regexp.test(d.establishment.siret)
                );
            });
        }
        if (filterActiveTab === "tous") {
            return dossiersTmp;
        }
        if (RolesEnum.SYNERPA === userRoleId) {
            if (filterActiveTab === "EN_COURS_EXAMEN") {
                return dossiersTmp.filter((d: any) => {
                    const status = d?.dossierStatus?.code
                        ? d?.dossierStatus?.code
                        : getDossierStatusOfDossier(d, dossiersStatus);
                    return (
                        d.assignedValidatorUser !== null &&
                        status !== DossierStatusEnum.FINALISE
                    );
                });
            }
            if (filterActiveTab === DossierStatusEnum.ATTENTE_DECISION) {
                return dossiersTmp.filter((d: any) => {
                    return d.assignedValidatorUser === null;
                });
            }
        }
        return dossiersTmp.filter((d: any) => {
            if (d?.dossierStatus?.code) {
                return d?.dossierStatus?.code === filterActiveTab;
            } else {
                const status = getDossierStatusOfDossier(d, dossiersStatus);
                return status?.code === filterActiveTab;
            }
        });
    };
    const initialStateFilterActiveTab = () => {
        if (
            undefined === dossierStatusFilter.dossierStatusFilter ||
            dossierStatusFilter.dossierStatusFilter.code.length === 0
        )
            return filterTabsMenu[0].key;
        const res = filterTabsMenu.find((d) => {
            return d.key === dossierStatusFilter.dossierStatusFilter.code;
        });
        return res ? res.key : filterTabsMenu[0].key;
    };
    const [filterActiveTab, setFilterActiveTab] = useState<string>(
        initialStateFilterActiveTab()
    );

    const [dossiers, setDossiers] = useState<Dossier[]>([]);
    const [referenciels, setReferentiels] = useState([]);
    const [establishements, setEstablishments] = useState<Establishment[]>([]);
    const [instructorList, setInstructorList] = useState<any>([]);
    const [validatorUsers, setValidatorUsers] = useState<AuthUser[]>([]);
    const { fetcher } = useFetch();

    const [establishementGroupSelected, setSelectedEstablishementGroup] =
        useState<number | null>(null);

    const { updateModal, updateIsCustomModalActive } =
        useContext(CustomModalContext);

    const {
        putDocsOnCommitementRequirements,
        downloadCertificate,
        establishmentGroups,
        fetchRegionList,
        regionList,
    } = useDossier();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const referencielsGroupedById = referenciels.length
        ? cloneObject(groupBy(referenciels, (ref: any) => ref.id))
        : null;
    const [searchQuery, setSearchQuery] = useState("");
    const dossiersFiltered = useMemo(() => {
        return dossiers.length
            ? filterDossiers(
                  filterActiveTab,
                  dossiers,
                  dossierStatus,
                  searchQuery
              )
            : [];
    }, [dossiers, filterActiveTab, dossierStatus, searchQuery]);

    const dataFiltered = useMemo(() => {
        return dossiersFiltered.length ? dossiersFiltered : [];
    }, [dossiersFiltered]);

    const establishementsGroupedBy = useMemo(() => {
        return groupBy(
            establishements,
            (establishment: EstablishmentDbType) => establishment.id
        );
    }, [establishements]);

    const validatorUsersGroupedById = validatorUsers.length
        ? groupBy(validatorUsers, (vUser: AuthUser) => vUser.id)
        : null;

    const dataFilteredAndEnriched = useMemo(() => {
        return dataFiltered.length
            ? dataFiltered.map((dossier: any) => {
                  if (!dossier.establishment.id) {
                      dossier.establishment =
                          establishementsGroupedBy[dossier.establishment][0];
                  }

                  if (!dossier.referenciel.id) {
                      const refFromDb = cloneObject(
                          referencielsGroupedById[dossier.referenciel][0]
                      );

                      const referenciel = {
                          ...refFromDb,
                          commitments: putDocsOnCommitementRequirements(
                              refFromDb.commitments,
                              dossier.documents,
                              dossier.establishment
                          ),
                      };
                      dossier.referenciel = referenciel;
                      delete dossier.documents;
                  }
                  if (!dossier.dossierStatus.id) {
                      dossier.dossierStatus = getDossierStatusOfDossier(
                          dossier,
                          dossierStatus
                      );
                  }
                  if (userRoleId === RolesEnum.SYNERPA) {
                      if (
                          dossier?.assignedValidatorUser &&
                          !dossier?.assignedValidatorUser.id &&
                          validatorUsersGroupedById
                      ) {
                          dossier.assignedValidatorUser =
                              validatorUsersGroupedById[
                                  dossier.assignedValidatorUser
                              ][0];
                      }
                  }

                  return dossier;
              })
            : [];
    }, [
        dataFiltered,
        dossierStatus,
        validatorUsersGroupedById,
        establishementsGroupedBy,
        referencielsGroupedById,
        instructorList,
    ]);

    const establishmentGroupsOptionsOfDossiers = useMemo(() => {
        return userRoleId === RolesEnum.SYNERPA
            ? establishmentGroups.filter((eg: EstablishmentCategory) =>
                  dataFilteredAndEnriched.find(
                      (dossier: DossierDetailType) =>
                          Number(dossier.establishment.establishmentGroup) ===
                          eg.id
                  )
              )
            : [];
    }, [establishmentGroups, dataFilteredAndEnriched]);

    const filterByEstablishmentGroup = (dataFilteredAndEnriched: any) => {
        if (userRoleId === RolesEnum.SYNERPA) {
            if (
                establishementGroupSelected &&
                establishementGroupSelected !== -1
            ) {
                return dataFilteredAndEnriched.filter(
                    (e: DossierDetailType) =>
                        Number(e.establishment.establishmentGroup) ===
                        establishementGroupSelected
                );
            }
        }

        return dataFilteredAndEnriched;
    };

    const dataFilteredAndEnrichedWithGroupement = useMemo(() => {
        return filterByEstablishmentGroup(dataFilteredAndEnriched);
    }, [dataFilteredAndEnriched]);

    const groupedDossiersByRefs = useMemo(() => {
        return dataFilteredAndEnrichedWithGroupement.length &&
            referencielsGroupedById
            ? groupBy(
                  dataFilteredAndEnrichedWithGroupement,
                  (dossier: any) => dossier.referenciel.id
              )
            : null;
    }, [dataFilteredAndEnrichedWithGroupement]);

    const fetchDossiers = useCallback(async () => {
        if (!currentUser.id) {
            return;
        }
        let dossiersRes = await getDossiers(currentUser.id, {
            userRoleId,
            dossiersStatuses: getDossierStatusesIdsByUserRole(
                userRoleId,
                dossierStatus
            ),
            establishmentGroup,
            region,
        });

        if (!dossiersRes.detail) {
            // Afficher les dossiers en cours de complétude même si les dossiers n'ont pas été soumis email de 22/04/2024 15:22 <raphael.faucher@socotec.com>

            // if (RolesEnum.ADMIN === userRoleId) {
            //     dossiersRes = dossiersRes.filter(
            //         (d: Dossier) => d.submittedAt !== null
            //     );
            // }
            const refesIds = dossiersRes
                .filter((d: Dossier) => d.referenciel)
                .map((d: Dossier) => d.referenciel)
                .filter(
                    (lb: number, index: number, self: number[]) =>
                        self.findIndex((o) => o === lb) === index
                );
            const refsRes = await getReferenciels(refesIds);
            if (!refsRes.detail) {
                const establishementIds = dossiersRes.map(
                    (d: Dossier) => d.establishment
                );

                const establishements = await getEstablishmentByIds(
                    establishementIds
                );

                if (!establishements.detail && !establishements.detail) {
                    setEstablishments(establishements);
                    setDossiers(dossiersRes);
                    setReferentiels(refsRes);
                    if (RolesEnum.ADMIN === userRoleId) {
                        const instructorListRes = await getInstructorList();
                        if (!instructorListRes.detail) {
                            setInstructorList(instructorListRes);
                        }
                    }
                    if (RolesEnum.SYNERPA === userRoleId) {
                        const validatorUserIds = dossiersRes
                            .map((d: Dossier) => d.assignedValidatorUser)
                            .filter((u: any) => u !== null);

                        if (validatorUserIds.length) {
                            const validatorUsers = await getUsers(
                                validatorUserIds
                            );

                            setValidatorUsers(validatorUsers);
                        }
                    }
                    setIsLoading(true);
                }
            }
        }
    }, []);

    const closeModal = () => {
        updateIsCustomModalActive(false);
    };

    const toBulkAssignedDossier = useCallback(
        async (dossierIdsToAssign: number[], instructeurId: number) => {
            closeModal();
            const doBulkAssign = async () => {
                const assignmentDossierRes = await dossiersAssignment(
                    dossierIdsToAssign,
                    instructeurId
                );
                if (!assignmentDossierRes.detail) {
                    setIsLoading(false);
                    await fetchDossiers();
                }
            };
            fetcher(doBulkAssign());
        },
        [dossiers, instructorList]
    );

    const openBulkAssignedDossierModal = useCallback(async () => {
        const soumisStatusId = (
            getDossierStatusByCode(DossierStatusEnum.SOUMIS, dossierStatus) ||
            {}
        ).id;

        const dossiersDetail: any = [...dossiers];

        const submittedDossiers = dossiersDetail.filter(
            (d: DossierDetailType) => d?.dossierStatus?.id === soumisStatusId
        );

        updateModal({
            title: "",
            sx: {
                paper: {
                    padding: 0,
                },
            },
            content: (
                <BulkAssigned
                    dossiers={submittedDossiers}
                    close={closeModal}
                    instructorList={instructorList}
                    toBulkAssignedDossier={toBulkAssignedDossier}
                />
            ),
        });
        updateIsCustomModalActive(true);
    }, [dossiers, instructorList]);

    const onEstablishmentGroupSelected = useCallback((e: any) => {
        setSelectedEstablishementGroup(e);
    }, []);

    useEffect(() => {
        fetchDossiers();
        scrollToTop();
    }, [fetchDossiers]);

    useEffect(() => {
        fetchRegionList();
    }, [fetchRegionList]);

    return (
        <DossiersLayout
            filterTabsMenu={filterTabsMenu}
            numberOfDossier={dataFilteredAndEnrichedWithGroupement.length}
            setFilterActiveTab={setFilterActiveTab}
            filterActiveTab={filterActiveTab}
            setSearchQuery={setSearchQuery}
            userRoleId={userRoleId}
            openBulkAssignedDossierModal={openBulkAssignedDossierModal}
            establishmentGroupsOptionsOfDossiers={
                establishmentGroupsOptionsOfDossiers
            }
            onEstablishmentGroupSelected={onEstablishmentGroupSelected}
            establishementGroupSelected={establishementGroupSelected}
        >
            {groupedDossiersByRefs && isLoading ? (
                <DossiersList
                    groupedDossiersByRefs={groupedDossiersByRefs}
                    instructorList={instructorList}
                    toBulkAssignedDossier={toBulkAssignedDossier}
                    downloadCertificate={downloadCertificate}
                    regionList={regionList}
                />
            ) : !dossiers.length &&
              [
                  RolesEnum.CLIENT,
                  RolesEnum.ADMIN_REGION,
                  RolesEnum.ADMIN_RESEAU,
              ].includes(userRoleId) ? (
                isLoading ? (
                    <EmptyDossiers />
                ) : null
            ) : null}
        </DossiersLayout>
    );
}
